/**
 * A deliberately small history router (~60 lines) instead of a routing
 * dependency: the portal has one level of flat routes, no nested layouts, no
 * data loaders, and Cloudflare Pages already serves index.html for every
 * unknown path (SPA mode, no 404.html), so all it needs is pattern matching,
 * `navigate` and a `Link` that keeps modifier-clicks working.
 *
 * Unsaved changes (third live review, 2026-09-27): a screen with a dirty form
 * registers `useUnsavedChangesGuard(dirty)`. Leaving it — a link, `navigate`
 * to another path, Back/Forward, or anything routed through `confirmLeave`
 * (the site switcher) — then asks "Discard unsaved changes?" in an in-app
 * dialog, and closing or reloading the tab gets the browser's own prompt
 * (`beforeunload`). Changing only the query string (a filter, a tab) is not
 * leaving. `navigate(to, { force: true })` skips the question for a caller
 * that has just saved (the setup footer's "Save draft and continue").
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { Modal } from './ui/kit';

export interface NavigateOptions {
  replace?: boolean;
  /** Skip the unsaved-changes question (the caller has saved or discarded). */
  force?: boolean;
  /** Keep the scroll position (a filter change on the same page). */
  keepScroll?: boolean;
}

interface RouterState {
  path: string;
  search: URLSearchParams;
  navigate: (to: string, options?: NavigateOptions) => void;
  /** Runs `action` now, or after the person confirms discarding unsaved changes. */
  confirmLeave: (action: () => void) => void;
  /** Registers a "has unsaved changes" check; returns the unregister function. */
  registerGuard: (isDirty: () => boolean) => () => void;
}

const RouterContext = createContext<RouterState | null>(null);

type Pending = { kind: 'navigate'; to: string; options?: NavigateOptions } | { kind: 'pop' } | { kind: 'action'; action: () => void };

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(() => ({ path: window.location.pathname, search: window.location.search }));
  const guards = useRef(new Set<() => boolean>());
  const [pending, setPending] = useState<Pending | null>(null);
  // Where we are, for undoing a Back/Forward that is held for confirmation.
  const here = useRef(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  // A confirmed Back/Forward replays through popstate; let that one through.
  const bypassPop = useRef(false);

  const dirty = useCallback(() => [...guards.current].some((g) => g()), []);

  useEffect(() => {
    const onPop = () => {
      const target = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const samePath = window.location.pathname === new URL(here.current, window.location.origin).pathname;
      if (!bypassPop.current && !samePath && dirty()) {
        // Put the page back and ask; "Discard" replays the step with history.back().
        window.history.pushState(null, '', here.current);
        setPending({ kind: 'pop' });
        return;
      }
      bypassPop.current = false;
      here.current = target;
      setLocation({ path: window.location.pathname, search: window.location.search });
    };
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!dirty()) return;
      e.preventDefault();
      // Chrome still needs returnValue set for the prompt to show.
      e.returnValue = '';
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [dirty]);

  const go = useCallback((to: string, options?: NavigateOptions) => {
    const url = new URL(to, window.location.origin);
    if (options?.replace) window.history.replaceState(null, '', url);
    else window.history.pushState(null, '', url);
    here.current = `${url.pathname}${url.search}${url.hash}`;
    setLocation({ path: url.pathname, search: url.search });
    if (!options?.keepScroll) window.scrollTo?.(0, 0);
  }, []);

  const navigate = useCallback(
    (to: string, options?: NavigateOptions) => {
      const url = new URL(to, window.location.origin);
      if (!options?.force && url.pathname !== window.location.pathname && dirty()) {
        setPending({ kind: 'navigate', to, options });
        return;
      }
      go(to, options);
    },
    [dirty, go],
  );

  const confirmLeave = useCallback(
    (action: () => void) => {
      if (dirty()) setPending({ kind: 'action', action });
      else action();
    },
    [dirty],
  );

  const registerGuard = useCallback((isDirty: () => boolean) => {
    guards.current.add(isDirty);
    return () => {
      guards.current.delete(isDirty);
    };
  }, []);

  const discard = () => {
    const p = pending;
    setPending(null);
    if (!p) return;
    if (p.kind === 'navigate') go(p.to, p.options);
    else if (p.kind === 'action') p.action();
    else {
      bypassPop.current = true;
      window.history.back();
    }
  };

  const value = useMemo(
    () => ({ path: location.path, search: new URLSearchParams(location.search), navigate, confirmLeave, registerGuard }),
    [location, navigate, confirmLeave, registerGuard],
  );
  return (
    <RouterContext.Provider value={value}>
      {children}
      <Modal
        open={pending !== null}
        title="Discard unsaved changes?"
        onClose={() => setPending(null)}
        footer={
          <>
            <button type="button" className="btn btn-primary" autoFocus onClick={() => setPending(null)}>
              Keep editing
            </button>
            <button type="button" className="btn btn-danger" onClick={discard}>
              Discard changes
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>You have changes on this page that aren’t saved. If you leave now, they’re lost.</p>
      </Modal>
    </RouterContext.Provider>
  );
}

/**
 * While `dirty` is true, leaving the page asks first (see the header). The
 * check reads the latest value through a ref, so a save that has just cleared
 * the form is seen on the very next navigation.
 */
export function useUnsavedChangesGuard(dirty: boolean): void {
  const ctx = useContext(RouterContext);
  const ref = useRef(dirty);
  ref.current = dirty;
  const register = ctx?.registerGuard;
  useEffect(() => {
    if (!register) return;
    return register(() => ref.current);
  }, [register]);
}

export function useRouter(): RouterState {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter outside RouterProvider');
  return ctx;
}

/** `/leads/:id` against `/leads/ld_1` → `{ id: 'ld_1' }`; null when it does not match. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const s = path.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i += 1) {
    const seg = p[i] as string;
    const val = s[i] as string;
    if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(val);
    else if (seg !== val) return null;
  }
  return params;
}

export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const { navigate } = useRouter();
  const handle = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={handle} {...rest} />;
}
